/**
 * Which tab of Entity Resolution is open, kept in the address (`?tab=`) so a tab can be linked
 * to and survives a reload. The page shares its search params with the case shell's document
 * overlay, so the tab is written beside the params that are there, never in place of them.
 */
import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

export const RESOLUTION_TAB_PARAM = 'tab'

/** Every tab, in bar order; Tattoo matching and Voice similarity are offered only while HUMINT is on. */
export const RESOLUTION_TABS = [
  'duplicates',
  'photoMatching',
  'tattooMatching',
  'voiceMatching',
] as const
export type ResolutionTab = (typeof RESOLUTION_TABS)[number]

/** The tabs of a deployment with Advanced Face Detection and without HUMINT. */
export const FACE_RESOLUTION_TABS: readonly ResolutionTab[] = ['duplicates', 'photoMatching']

/** The tab the address names; Duplicates when it names none, or one the page does not offer. */
export function resolutionTab(
  params: URLSearchParams,
  offered: readonly ResolutionTab[]
): ResolutionTab {
  const asked = params.get(RESOLUTION_TAB_PARAM)
  return offered.find(tab => tab === asked) ?? 'duplicates'
}

/** The params with `tab` named, every other param kept. */
export function withResolutionTab(params: URLSearchParams, tab: string): URLSearchParams {
  const next = new URLSearchParams(params)
  next.set(RESOLUTION_TAB_PARAM, tab)
  return next
}

/** The open tab and the way to another; a tab change replaces the address and adds no history. */
export function useResolutionTab(
  offered: readonly ResolutionTab[]
): [ResolutionTab, (tab: string) => void] {
  const [params, setParams] = useSearchParams()
  const open = useCallback(
    (tab: string) => setParams(previous => withResolutionTab(previous, tab), { replace: true }),
    [setParams]
  )
  return [resolutionTab(params, offered), open]
}
